import React, { ForwardedRef } from "react";

import { forwardRef } from "react";
import { Field, FieldProps } from "formik";
import {
  TextField,
  InputAdornment,
  InputProps,
  TextFieldProps,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

// Datepicker
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import InfoMsg from "../InfoMsg";

export type FormikDatePickerProps = ReactDatePickerProps & {
  name: string;
  label: string;
  color?:
    | "primary"
    | "info"
    | "error"
    | "secondary"
    | "success"
    | "warning"
    | undefined;
  variant?: "outlined" | "filled" | "standard" | undefined;
  size?: "small" | "medium" | undefined;
  fullWidth?: boolean;
  inputProps?: InputProps;
  info?: string;
};

type CalendarInputProps = TextFieldProps & {
  fieldProps: FieldProps["field"];
  info?: string;
};

const CalendarInput = forwardRef(
  (
    {
      onChange,
      placeholder,
      value,
      id,
      onClick,
      error,
      helperText,
      required,
      label,
      info,
      color,
      size,
      inputProps,
      fullWidth,
      variant,
      fieldProps,
    }: CalendarInputProps,
    ref
  ) => (
    <Box>
      <Stack direction="row">
        <Typography
          component="span"
          variant="subtitle2"
          className={`${required ? "field-required" : ""}`}
        >
          {label}
        </Typography>
        {info && <InfoMsg info={info} />}
      </Stack>
      <TextField
        ref={ref as ForwardedRef<HTMLDivElement>}
        id={id}
        inputProps={inputProps}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <div onClick={onClick} style={{ cursor: "pointer" }}>
                <CalendarMonthIcon />
              </div>
            </InputAdornment>
          ),
        }}
        variant={variant}
        fullWidth={fullWidth}
        size={size}
        color={color}
        error={error}
        helperText={helperText}
        {...fieldProps}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
      />
    </Box>
  )
);

const FormikDatePicker = (props: FormikDatePickerProps) => {
  const {
    name,
    label,
    info,
    color = "primary",
    size = "small",
    inputProps = {},
    fullWidth = true,
    variant = "outlined",
    ...otherProps
  } = props;

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => {
        const { value } = field;
        const { setFieldValue } = form;
        const error = meta.touched && meta.error ? true : false;
        const helperText = error ? <>{meta.error}</> : null;

        return (
          <DatePicker
            id={name}
            name={name}
            dateFormat="dd/MM/yyyy"
            {...otherProps}
            value={value}
            customInput={
              <CalendarInput
                label={label}
                color={color}
                error={error}
                size={size}
                info={info}
                InputProps={inputProps}
                helperText={helperText}
                fullWidth={fullWidth}
                variant={variant}
                fieldProps={{ ...field }}
              />
            }
            selected={value}
            onChange={(val) => setFieldValue(name, val)}
          />
        );
      }}
    </Field>
  );
};

export default FormikDatePicker;
